import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, Loader2, Mail } from 'lucide-react';
import { getDisplayErrorMessage, getErrorCode } from '@/lib/api';
import {
  INVITE_LINK_COPY_EN, fillInviteCopy, inviteExpiryDate, inviteLinkErrorText, inviteLinkMailto,
  type InviteLink, type InviteLinkCopy,
} from '@/lib/inviteLink';
import { useAuthStore } from '@/store/auth';
import { Modal } from './Modal';
import formStyles from './Form.module.css';
import styles from './InviteLinkDialog.module.css';

/**
 * Copy invite link's dialog (Warren, 29 October 2026; the rules are in
 * lib/inviteLink.ts). Clicking the action calls that invite type's link
 * endpoint and opens this: the link in a read-only field, Copy (the clipboard,
 * with a select-all fallback), "Email from my account" (a mailto: to the
 * invitee with a short note in the operator's voice, the link and its
 * expiry), and the line saying it replaces earlier invite emails, works once
 * and when it expires. Closing discards the link; it is never shown again (it
 * lives only in this component's state). An error shows inline with the
 * Api's reason; an expired invite says to re-send first.
 */

export interface InviteLinkTarget { email: string; name?: string | null }

interface DialogState { target: InviteLinkTarget; link: InviteLink | null; error: string }

/**
 * The action's state for one screen: open(target, fetch) calls the endpoint
 * and shows the dialog; `dialog` is the element to render; `busyFor` names the
 * invitee whose link is being made.
 */
export function useInviteLinkDialog(options: { copy?: InviteLinkCopy; locale?: string } = {}) {
  const copy = options.copy ?? INVITE_LINK_COPY_EN;
  const [state, setState] = useState<DialogState | null>(null);
  // A response that lands after the dialog closed (or another opened) is dropped, never shown.
  const current = useRef(0);
  const close = useCallback(() => { current.current += 1; setState(null); }, []);
  const open = useCallback(async (target: InviteLinkTarget, fetchLink: () => Promise<InviteLink>) => {
    const mine = (current.current += 1);
    setState({ target, link: null, error: '' });
    try {
      const link = await fetchLink();
      if (current.current === mine) setState({ target, link, error: '' });
    } catch (err) {
      const text = inviteLinkErrorText(await getErrorCode(err), await getDisplayErrorMessage(err, copy.failed), copy);
      if (current.current === mine) setState({ target, link: null, error: text });
    }
  }, [copy]);
  const dialog: ReactNode = state
    ? <InviteLinkDialog target={state.target} link={state.link} error={state.error} copy={copy} locale={options.locale} onClose={close} />
    : null;
  return { open, close, dialog, busyFor: state && !state.link && !state.error ? state.target.email : null };
}

export function InviteLinkDialog({ target, link, error, copy = INVITE_LINK_COPY_EN, locale, onClose }: {
  target: InviteLinkTarget; link: InviteLink | null; error: string; copy?: InviteLinkCopy; locale?: string; onClose: () => void;
}) {
  const field = useRef<HTMLInputElement | null>(null);
  const [copied, setCopied] = useState<'no' | 'yes' | 'selected'>('no');
  const sender = useAuthStore((s) => s.user?.name ?? null);

  const copyLink = async (): Promise<void> => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied('yes');
    } catch {
      // No clipboard (an insecure context, a refused permission): select the
      // whole link so the keyboard copies it, and try the older copy command.
      field.current?.focus();
      field.current?.select();
      let done = false;
      try { done = document.execCommand('copy'); } catch { done = false; }
      setCopied(done ? 'yes' : 'selected');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={copy.title}
      size="md"
      footer={<button type="button" className={formStyles.btnSecondary} onClick={onClose}>{copy.close}</button>}
    >
      <div className={styles.body}>
        <p className={styles.who}>{target.name ? `${target.name}, ${target.email}` : target.email}</p>
        {!link && !error && (
          <p className={styles.status} role="status"><Loader2 size={14} className={styles.spin} /> {copy.loading}</p>
        )}
        {error && <div className={formStyles.formError} role="alert">{error}</div>}
        {link && (
          <>
            <label className={formStyles.label} htmlFor="invite-link-url">{copy.field}</label>
            <input
              id="invite-link-url"
              ref={field}
              className={formStyles.input}
              type="text"
              value={link.url}
              readOnly
              spellCheck={false}
              onFocus={(e) => e.currentTarget.select()}
            />
            <div className={styles.actions}>
              <button type="button" className={formStyles.btnPrimary} onClick={() => { void copyLink(); }}>
                {copied === 'yes' ? <><Check size={13} /> {copy.copied}</> : <><Copy size={13} /> {copy.copy}</>}
              </button>
              <a
                className={formStyles.btnSecondary}
                href={inviteLinkMailto({ to: target.email, url: link.url, expiresAt: link.expires_at, inviteeName: target.name, senderName: sender, copy, locale })}
              >
                <Mail size={13} /> {copy.email}
              </a>
            </div>
            {copied === 'selected' && <p className={styles.hint} role="status">{copy.selected}</p>}
            <p className={styles.note}>
              {link.expires_at ? fillInviteCopy(copy.replaces, { date: inviteExpiryDate(link.expires_at, locale) }) : copy.signInPage}
            </p>
            <p className={styles.hint}>{copy.discard}</p>
          </>
        )}
      </div>
    </Modal>
  );
}
